<script setup>
import notification from "@/utils/plugins/NotificationService";
import { computed, ref, watch } from "vue";
import PresenceAbsenceForm from "@/components/shared/Presence/PresenceAbsenceForm.vue";
import { useUiStore } from "@/app/stores/uiStore";
import PresenceService from "@/api/services/newPresencia.service";
import { absenceApiError, toMadridInput } from "@/utils/presenceAbsence";

// eslint-disable-next-line no-undef
const props = defineProps({ modelValue: Boolean });
// eslint-disable-next-line no-undef
const emit = defineEmits(['update:modelValue', 'confirm', 'close']);
const uiStore = useUiStore();
const modelValue = computed(() => props.modelValue);
const reasons = [
    { label: "Descanso", value: "break" },
    { label: "Cita con el médico", value: "medical_appointment" },
    { label: "Conciliación de hijos", value: "child_conciliation" },
    { label: "Conciliación familiar", value: "family_conciliation" },
    { label: "Otros", value: "other" },
];
const stopReason = ref('break');
const otherReason = ref('');
const saving = ref(false);
const showAbsence = ref(false);
const errorMessage = ref('');
const absenceError = ref('');
const coveredByAbsence = ref(null);
const canGenerateAbsence = computed(() => ['medical_appointment', 'other'].includes(stopReason.value));

watch(() => props.modelValue, (opened, _previous, onCleanup) => {
    if (!opened || typeof document === 'undefined') return;
    const main = document.querySelector('main.main-content');
    document.body.classList.add('presence-modal-open');
    main?.classList.add('presence-modal-open');
    onCleanup(() => {
        document.body.classList.remove('presence-modal-open');
        main?.classList.remove('presence-modal-open');
    });
}, { immediate: true, flush: 'sync' });

watch(() => props.modelValue, (opened) => {
    if (!opened) return;
    stopReason.value = 'break';
    otherReason.value = '';
    errorMessage.value = '';
    absenceError.value = '';
    coveredByAbsence.value = null;
    showAbsence.value = false;
});
watch(stopReason, () => {
    errorMessage.value = '';
    coveredByAbsence.value = null;
});
const closeModal = () => {
    if (saving.value) return;
    showAbsence.value = false;
    emit('update:modelValue', false);
    emit('close');
};

function handleError(error) {
    const code = error?.response?.data?.errorCode;
    if (canGenerateAbsence.value && ['absence_required', 'absence_invalid', 'absence_overlap'].includes(code)) {
        showAbsence.value = true;
    }
    if (showAbsence.value) absenceError.value = absenceApiError(error);
    else errorMessage.value = absenceApiError(error);
}

// Only this operation writes: the backend creates the pause and absence atomically.
async function savePause(absence) {
    const params = { pauseReason: stopReason.value };
    if (stopReason.value === 'other') {
        params.pauseReasonOtherText = absence ? absence.observations : otherReason.value.trim();
    }
    if (absence) params.absence = absence;
    const response = await PresenceService.pauseWorkRecordsUser(params);
    if (response?.status !== 201) throw new Error('No se ha podido confirmar la parada.');
    let message = absence ? 'Presencia parada y ausencia registrada correctamente' : 'Presencia parada';
    if (!absence && coveredByAbsence.value) {
        message += '. Ya tienes una ausencia registrada de ' +
            toMadridInput(coveredByAbsence.value.fromDate) + ' a ' + toMadridInput(coveredByAbsence.value.toDate) + '.';
    }
    notification.addNotification({ type: 'success', message, duration: 6000 });
    showAbsence.value = false;
    emit('update:modelValue', false);
    emit('confirm', params);
    emit('close');
}

const confirmStop = async () => {
    if (saving.value || !stopReason.value) return;
    errorMessage.value = '';
    if (stopReason.value === 'other' && !otherReason.value.trim()) {
        errorMessage.value = 'Indique la razón de la parada.';
        return;
    }
    saving.value = true;
    uiStore.startLoading();
    try {
        if (canGenerateAbsence.value) {
            const response = await PresenceService.checkPauseAbsence(stopReason.value);
            if (typeof response?.data?.requiresAbsence !== 'boolean') {
                throw new Error('No se ha podido comprobar si necesitas una ausencia. Inténtelo de nuevo.');
            }
            coveredByAbsence.value = response.data.coveredByAbsence;
            if (response.data.requiresAbsence) {
                absenceError.value = '';
                showAbsence.value = true;
                return;
            }
        }
        await savePause();
    } catch (error) {
        handleError(error);
    } finally {
        saving.value = false;
        uiStore.stopLoading();
    }
};

const confirmAbsence = async (absence) => {
    if (saving.value) return;
    saving.value = true;
    absenceError.value = '';
    uiStore.startLoading();
    try {
        await savePause(absence);
    } catch (error) {
        handleError(error);
    } finally {
        saving.value = false;
        uiStore.stopLoading();
    }
};
</script>

<template>
    <el-dialog :model-value="modelValue && !showAbsence" width="500" :show-close="false" :lock-scroll="true" :append-to-body="true"
        class="stop-presence-dialog" :close-on-click-modal="!saving" :close-on-press-escape="!saving"
        :before-close="closeModal">
        <h3>Razón de parada:</h3>
        <el-radio-group v-if="modelValue" v-model="stopReason" class="reason-group" :disabled="saving">
            <el-radio v-for="reason in reasons" :key="reason.value" :value="reason.value">
                {{ reason.label }}
            </el-radio>
        </el-radio-group>
        <el-input v-if="stopReason === 'other'" v-model="otherReason" type="textarea" placeholder="Indique la razón"
            class="mt-3 w-100" :disabled="saving" />

        <p v-if="errorMessage" class="text-danger mt-3 mb-0" role="alert">{{ errorMessage }}</p>
        <div class="stop-actions d-flex justify-content-end gap-3 mt-4">
            <button class="btn btn_red px-4" :disabled="saving" @click="closeModal">
                Cancelar
            </button>
            <button class="btn btn_yellow px-4" :disabled="saving" @click="confirmStop">
                Guardar
            </button>
        </div>
    </el-dialog>
    <el-dialog :model-value="showAbsence" title="Nueva Ausencia" width="90%"
        class="medical-absence-dialog" :append-to-body="true" :show-close="false" :lock-scroll="true"
        :close-on-click-modal="!saving" :close-on-press-escape="!saving"
        :before-close="closeModal">
        <template #header>
            <div class="medical-absence-header">
                <div class="medical-absence-heading">
                    <svg class="medical-absence-asterisk" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 724 1024">
                        <path d="M0 499.8l59-181.7 332.5 108.6-59 180.8-332.5-107.7zM79.6 744.3l206.1-282.9 154.5 111.5-206.1 283.8-154.5-112.4zM266.9 167.3h191.1v350.3h-191.1v-350.3zM285.6 572.9l154.5-111.5 206.1 282.9-154.5 112.4-206.1-283.8zM332.5 426.8l332.5-108.6 59 181.7-331.6 107.7-59.9-180.8z" />
                    </svg>
                    <h2>Nueva Ausencia</h2>
                </div>
                <button type="button" class="medical-absence-close" aria-label="Cerrar nueva ausencia"
                    :disabled="saving" @click="closeModal">
                    <img src="@/assets/images/aspa_blue_small_entrada.png" alt="" />
                </button>
            </div>
        </template>
        <PresenceAbsenceForm v-if="showAbsence" :pause-reason="stopReason"
            :initial-observations="stopReason === 'other' ? otherReason : ''"
            :busy="saving" :error="absenceError" @confirm="confirmAbsence" @cancel="closeModal" />
    </el-dialog>
</template>

<style lang="scss" scoped>
.medical-absence-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding-top: 2px;
}
.medical-absence-heading {
    position: relative;
    min-width: 0;

    h2 {
        margin: 16px 0 0;
        text-align: left;
        font-size: 4em;
        font-weight: 900;
        line-height: 1.2;
        letter-spacing: -0.05em;
        color: #f9156f;
    }
}
.medical-absence-asterisk {
    position: absolute;
    top: -8px;
    left: 0;
    width: 2.2em;
    height: auto;
    fill: #f9156f;
}
.medical-absence-close {
    flex: 0 0 8%;
    min-width: 44px;
    min-height: 44px;
    padding: 0;
    border: 0;
    background: transparent;

    img {
        display: block;
        width: 100%;
        height: auto;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }

    &:focus-visible {
        outline: 2px solid #409eff;
        outline-offset: 4px;
    }
}
.reason-group {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-top: 10px;
}

.stop-actions button {
    min-height: 44px;
}
@media (max-width: 767px) {
    .medical-absence-heading h2 {
        font-size: 2em;
        margin-top: 0;
    }
    .medical-absence-asterisk {
        display: none;
    }
    .stop-actions {
        flex-direction: column;
    }
    .stop-actions button {
        width: 100%;
        margin: 0;
    }
}
</style>

<style lang="scss">
body.presence-modal-open,
main.main-content.presence-modal-open {
    overflow-y: hidden;
}

.el-dialog.medical-absence-dialog {
    max-width: 816px;
    border-radius: 20px;

    .el-dialog__header {
        margin-right: 0;
    }
}
.el-dialog.stop-presence-dialog {
    border-radius: 20px;
    max-width: calc(100vw - 24px);
}
</style>
